<template>
  <div class="zone-tree">
    <div class="zone-tree__title">{{ $t('search-filters.zone.name') }}</div>
    <div class="zone-tree__country">
      <FormCheckbox
        class="zone-tree__checkbox"
        :label="$t(`search-filters.countries.values.CANADA`)"
        :value="hasAllCanada"
        :indeterminate="!hasAllCanada && hasAtLeastOneCanada"
        @input="onCanadaChange"
        size="small"
      />
    </div>
    <div class="zone-tree__regions">
      <FormCheckbox
        v-for="zone in tempCanadaZones"
        :key="zone.name"
        class="zone-tree__checkbox"
        :label="$t(`search-filters.zone.values.${zone.name}`)"
        :value="zone.value"
        @input="onZoneChange(zone, 'CANADA')"
        size="small"
      />
    </div>
    <div class="zone-tree__country">
      <FormCheckbox
        class="zone-tree__checkbox"
        :label="$t(`search-filters.countries.values.UNITED_STATES`)"
        :value="hasAtLeastOneUSA"
        @input="onUSAChange"
        size="small"
      />
    </div>
  </div>
</template>

<script>
import FormCheckbox from '@/components/form/FormCheckbox';

import { ZONES_CANADA_KEYS } from '@/models/search-filters';

import { mapGetters } from 'vuex';

export default {
  name: 'ZoneTree',
  components: {
    FormCheckbox,
  },
  data() {
    return {
      tempZones: {},
      tempCanadaZones: {},
      tempUSAZones: {},
    };
  },
  props: {
    searchFilters: {
      type: Object,
      required: true,
    },
  },
  computed: {
    hasAllCanada() {
      return this.tempCanadaZones.every((zone) => zone.value === true);
    },
    hasAtLeastOneCanada() {
      return this.tempCanadaZones.find((zone) => zone.value === true) !== undefined;
    },
    hasAtLeastOneUSA() {
      return this.tempUSAZones.value === true;
    },
    zones() {
      return this.searchFilters.zones;
    },
  },
  methods: {
    buildCanadaZones() {
      return ZONES_CANADA_KEYS.map((zone) => ({
        name: zone,
        value: this.zones.CANADA ? Object.keys(this.zones.CANADA).includes(zone) : false,
      }));
    },
    buildUSAZones() {
      return {
        name: 'UNITED_STATES',
        value: this.zones.UNITED_STATES ? this.zones.UNITED_STATES !== undefined : false,
      };
    },
    onChange() {
      this.$emit('onchange', this.tempZones);
    },
    onCanadaChange() {
      if (this.hasAllCanada) {
        this.tempCanadaZones.forEach((zone) => (zone.value = false));
        delete this.tempZones['CANADA'];
      } else {
        this.tempCanadaZones.forEach((zone) => (zone.value = true));
        // Track every province internally so the checkboxes, count and dirty-state reflect the
        // selection; expandToZones collapses "all provinces" back to { Canada: {} } on the wire.
        this.tempZones['CANADA'] = this.tempCanadaZones.reduce((acc, zone) => {
          acc[zone.name] = [];
          return acc;
        }, {});
      }
      this.onChange();
    },
    onUSAChange() {
      this.tempUSAZones.value = !this.tempUSAZones.value;

      if (this.tempUSAZones.value) {
        this.tempZones[`${this.tempUSAZones.name}`] = {};
      } else {
        delete this.tempZones[`${this.tempUSAZones.name}`];
      }
      this.onChange();
    },
    onZoneChange(zone, country) {
      zone.value = !zone.value;

      if (zone.value) {
        if (!this.tempZones[`${country}`]) this.tempZones[`${country}`] = {};
        this.tempZones[`${country}`][`${zone.name}`] = [];
      } else {
        delete this.tempZones[`${country}`][`${zone.name}`];
      }
      this.onChange();
    },
    clearSelection() {
      this.tempCanadaZones.forEach((zone) => (zone.value = false));
      this.tempUSAZones.value = false;
      this.tempZones = {};
    },
  },
  created() {
    this.tempCanadaZones = this.buildCanadaZones();
    this.tempUSAZones = this.buildUSAZones();
    this.tempZones = JSON.parse(JSON.stringify(this.zones));
  },
};
</script>

<style lang="scss">
// Self-contained styling so ZoneTree renders correctly wherever it is embedded
// (the tenders zones drawer and PageExplore's advanced dialog), without relying
// on the global .filter-advanced__* rules.
.zone-tree {
  &__title {
    font-size: 20px;
    font-weight: bold;
  }

  // Countries (Canada / United States) read as top-level parents.
  &__country {
    padding-top: 16px;

    .label__text {
      font-weight: 800;
      color: $color-normal;
    }
  }

  // Provinces are children of Canada: indented beneath it with a vertical
  // tree guide line.
  &__regions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 6px 0 6px 11px;
    padding: 0 0 0 22px;
    border-left: 1px solid $color-border;
  }

  &__checkbox {
    width: 240px;
    padding: 16px 0;
  }

  // Phones: one region per line, each a 44px line to tap (its label is the checkbox's).
  @include screen-below('tablet') {
    &__country {
      padding-top: 8px;
    }

    &__checkbox {
      width: 100%;
      padding: 0;

      .form-checkbox__label {
        min-height: 44px;
      }
    }
  }
}
</style>
